import { useMemo, useState, useEffect } from 'react';
import { useMeasure } from 'react-use';
import { identity } from 'lodash';

import SidePanel, { PanelMainGroup, PanelSection } from '../SidePanel';
import { usePortfolioEvaluatorContext } from '../../contexts/PortfolioEvaluatorContext';
import { asDecimal, asQuantity } from '../../utils/formatters';
import { useUnits } from '../../contexts/UnitContext';
import { LONG_AREA_UNITS } from '../../constants/units';
import { HoverNavItem } from '../left-menu/menu-components';
import VirtualizedList, {
  useVirtualizedScrollTo,
} from '../basics/VirtualizedList';
import { fitMapToFeature } from './helpers';

const ControlPanel = () => {
  const {
    features: allFeatures,
    map,
    hoveredFeatureId,
    setHoveredFeatureId,
    selectedFeatureId,
    setSelectedFeatureId,
  } = usePortfolioEvaluatorContext();

  const { displayUnit, transformToUnit = identity } = useUnits(LONG_AREA_UNITS);

  const rowRenderer = ({ index, data, style }) => {
    const feature = data[index];
    const { id, name, area, usState } = feature.properties;
    const transformedArea = transformToUnit(area);
    const areaString =
      transformedArea > 20
        ? asQuantity(transformedArea)
        : asDecimal(transformedArea);

    const LAST_ELEMENT_BOTTOM_PADDING = 12;

    return (
      <div
        // `style` is generated by VirtualizedList. Additionally, we add bottom padding to the last item
        style={{
          ...style,
          height: `${
            // see https://github.com/bvaughn/react-window/blob/efad3d8909753fd74aad7c47dc902b26f0919651/README.md#can-i-add-padding-to-the-top-and-bottom-of-a-list
            parseFloat(style.height) + LAST_ELEMENT_BOTTOM_PADDING
          }px`,
        }}
      >
        <HoverNavItem
          // NOTE: we use margin-right here to ensure the scroll bar doesn't overlap the items
          className="mr-2 flex-column"
          onClick={() => {
            if (!map) return;
            fitMapToFeature(map, feature);
            setSelectedFeatureId(id);
          }}
          onMouseEnter={() => setHoveredFeatureId(id)}
          onMouseLeave={() => setHoveredFeatureId(null)}
          hovered={hoveredFeatureId === id}
          isActive={selectedFeatureId === id}
        >
          <div className="w-100 text-truncate" title={name}>
            {name}
          </div>
          <div className="text-muted w-100">
            {areaString} {displayUnit},{' '}
            {usState?.abbreviation || 'Unknown State'}
          </div>
        </HoverNavItem>
      </div>
    );
  };

  const [searchString, setSearchString] = useState('');
  const features = useMemo(() => {
    const search = searchString.trim().toLowerCase();
    if (search.length === 0) return allFeatures;

    return allFeatures.filter((f) =>
      (f.properties.name ?? 'Unknown').toLowerCase().includes(search),
    );
  }, [allFeatures, searchString]);

  const [listParentRef, { height: listParentHeight }] = useMeasure();
  const TITLE_HEIGHT = 24; // Height of the <h4> element
  const INPUT_HEIGHT = 38; // Height of the <input> element
  const listContainerHeight = listParentHeight - TITLE_HEIGHT - INPUT_HEIGHT;

  // Scroll to select item in list when selectedFeatureId changes
  const { listRef, scrollTo } = useVirtualizedScrollTo();
  useEffect(() => {
    if (selectedFeatureId == null || listRef.current == null) return;

    const index = features.findIndex(
      (f) => f.properties.id === selectedFeatureId,
    );
    if (index === -1) return;

    scrollTo(index);
  }, [listRef, scrollTo, selectedFeatureId, features]);

  return (
    <SidePanel left>
      <PanelMainGroup>
        {/**
         * NOTE: we have 0 padding-right here so that the scroll bar ends up all
         * the way to the right, and then we use margin-right on the items to
         * ensure they aren't under the scrollbar.
         */}
        <PanelSection
          className="h-100 d-flex flex-column pl-2 pr-0 pb-0"
          ref={listParentRef}
        >
          {allFeatures.length > 0 && (
            <>
              <h4>Farms</h4>
              <input
                aria-label="Search farms"
                className="mr-2 mb-2 py-1"
                style={{ paddingLeft: '10px' }}
                placeholder="Type to filter farms..."
                value={searchString}
                onChange={(evt) => {
                  setSearchString(evt.target.value);
                }}
              />
              <VirtualizedList
                ref={listRef}
                height={listContainerHeight}
                items={features}
                rowRenderer={rowRenderer}
                itemHeight={42}
              />
            </>
          )}
        </PanelSection>
      </PanelMainGroup>
    </SidePanel>
  );
};

export default ControlPanel;
